/**
 * processPaletteTree.js (E2, §8.8) — la proyección PURA del `ProcessTreeDto` de P1 (§8.4) en las
 * filas del árbol desplegable de la sección Template de la paleta (CA1.2, S4, S6).
 *
 * Sin React, named exports y un único fichero de test, igual que sus hermanos `bomTree.js` y
 * `processTree.js`. Aquí no se pide nada al servidor, no se pinta nada y no se escribe en el modelo
 * del editor: se traduce el DTO a lo que la sección necesita para pintar y lo que `WorkflowFlow`
 * necesita para soltar.
 *
 * Tres cosas que este módulo decide y el resto solo consume:
 *   1. **Qué es cada fila** (`nodeType` JOB hoja / WORKFLOW rama) y si está vinculada (`linked`).
 *   2. **Qué rama no se pudo leer** (`unreadableBranchNodeIds`, `RN-01.3b`): se DECLARA, no se
 *      oculta, y no es arrastrable porque no hay subproceso que incorporar.
 *   3. **Qué le dice la fila al arrastre** (`toProcessDropPayload`): el nodo y el workflow que lo
 *      contiene —lo que la intención `COPY` necesita (§8.3)— y el código de la plantilla RAÍZ como
 *      proceso de origen (`RN-01.10`: nunca el del subproceso intermedio).
 *
 * El censo de HU-03 **no se calcula aquí**: lo calcula `compositionCensus.js` (E3) sobre el modelo
 * vivo del editor. Lo que este módulo pone es el lado del ÁRBOL de la cobertura: el TOTAL
 * (`censusTotalOf`, contando solo `countsInCensus`, `RN-03.4`), si ese total es PARCIAL por llevar
 * dentro una rama no legible, la cobertura `presentes/total` ya casada con el censo
 * (`censusCoverageOf`, `RN-03.5`) y las filas que sobreviven al filtro "pendientes de añadir"
 * (`pendingProcessRows`, `RN-03.8b`).
 *
 * @typedef {Object} ProcessPaletteRow
 * @property {string} nodeId
 * @property {string} code
 * @property {string} name
 * @property {'JOB'|'WORKFLOW'} nodeType
 * @property {number} depth Nivel de anidamiento, 0 en el primer nivel
 * @property {number} workflowId El workflow que CONTIENE la fila (lo que la copia necesita)
 * @property {number} subWorkflowId Solo en una fila WORKFLOW; 0 si no la tiene
 * @property {string} operationCode Solo en un JOB vinculado; '' si es local
 * @property {number} operationVersionNumber 0 si no hay vínculo
 * @property {string} linkState '' si el nodo es embebido
 * @property {boolean} linked
 * @property {boolean} countsInCensus
 * @property {number} timeMs Story 167 (C3): what the operation lasts, in ms; 0 = unknown (and always 0 in a WORKFLOW row)
 * @property {boolean} unreadable Rama declarada como no cargada (`RN-01.3b`)
 * @property {boolean} draggable
 * @property {ProcessPaletteRow[]} children
 */
import { contractDurationMs } from './libraryDuration'

import { toProcessVersionNumber, toSourceProcessPair } from './processOriginHighlight'

/** Lo que nace al soltar la fila (§8.3): un vínculo, una copia local o un subproceso. */
export const PROCESS_ROW_DROP_KIND = {
  LINKED_JOB: 'LINKED_JOB',
  LOCAL_JOB: 'LOCAL_JOB',
  PROCESS: 'PROCESS'
}

/** Los dos estados de vínculo VIVO (§8.3): los únicos que hacen de un JOB una operación presente. */
const LIVE_LINK_STATES = ['SYNCHRONIZED', 'CUSTOMIZED']

const toRow = (dto, depth, unreadable) => {
  const nodeId = dto?.nodeId || ''
  const nodeType = dto?.nodeType === 'WORKFLOW' ? 'WORKFLOW' : 'JOB'
  const isUnreadable = unreadable.has(nodeId)
  const operationCode = dto?.operationCode || ''
  const linkState = dto?.linkState || ''
  return {
    nodeId,
    code: dto?.code || '',
    name: dto?.name || '',
    nodeType,
    depth,
    workflowId: dto?.workflowId || 0,
    subWorkflowId: dto?.subWorkflowId || 0,
    operationCode,
    operationVersionNumber: dto?.operationVersionNumber || 0,
    linkState,
    // Un JOB con vínculo VIVO. `countsInCensus` lo dice ya el servidor (D11-bis); esto es lo mismo
    // leído del estado, para que la fila pueda pintarse sin depender de un booleano ausente.
    linked: nodeType === 'JOB' && operationCode !== '' && LIVE_LINK_STATES.includes(linkState),
    countsInCensus: dto?.countsInCensus === true,
    // Story 167 (C3, §7): what the job of this node lasts, as Sun publishes it; null/absent → 0 = unknown.
    timeMs: nodeType === 'JOB' ? contractDurationMs(dto?.timeMs) : 0,
    // PO, 29/09/2026: what the row shows and what the drag preview draws — the technology of a JOB and the code of
    // the sub-workflow a WORKFLOW row points at (the node it becomes is labelled with it). '' when Sun does not say.
    technologyName: nodeType === 'JOB' ? dto?.technologyName || '' : '',
    subWorkflowCode: nodeType === 'WORKFLOW' ? dto?.subWorkflowCode || '' : '',
    unreadable: isUnreadable,
    // RN-01.4: se arrastra el proceso completo (una rama) o cualquier JOB, a cualquier profundidad.
    // Una rama NO LEGIBLE no: no hay subproceso que incorporar (S6).
    draggable: !isUnreadable,
    children: (dto?.children || []).map((child) => toRow(child, depth + 1, unreadable))
  }
}

/**
 * S4 — Proyecta el `ProcessTreeDto` completo. Un DTO vacío o a medias produce un árbol vacío y
 * declarado, nunca un fallo: la paleta es una superficie de lectura (POL6).
 *
 * @param {Object} dto El `ProcessTreeDto` de P1
 * @returns {{ workflowId: number, code: string, name: string, description: string,
 *   rows: ProcessPaletteRow[], unreadableBranchNodeIds: string[] }}
 */
export const buildProcessPaletteTree = (dto = {}) => {
  const unreadableBranchNodeIds = (dto?.unreadableBranchNodeIds || []).map((nodeId) => String(nodeId))
  const unreadable = new Set(unreadableBranchNodeIds)
  return {
    workflowId: dto?.workflowId || 0,
    code: dto?.code || '',
    // S95 (D12, §8.4): la versión de la fila de plantilla viene UNA VEZ, en la raíz del DTO —el
    // árbol entero pertenece a esa fila—, y es DECIMAL. Sin ella no hay par que sellar.
    processVersionNumber: toProcessVersionNumber(dto?.processVersionNumber),
    name: dto?.name || '',
    description: dto?.description || '',
    rows: (dto?.root || []).map((child) => toRow(child, 0, unreadable)),
    unreadableBranchNodeIds
  }
}

/**
 * S24 — La fila con ese `nodeId`, a cualquier profundidad. Sin coincidencia devuelve `{}`: el
 * repo no usa `null` en lógica de negocio.
 *
 * @param {ProcessPaletteRow[]} rows
 * @param {string} nodeId
 * @returns {ProcessPaletteRow|Object}
 */
export const findProcessRow = (rows = [], nodeId = '') => {
  for (const row of rows || []) {
    if (row.nodeId === nodeId) {
      return row
    }
    const found = findProcessRow(row.children || [], nodeId)
    if (found.nodeId) {
      return found
    }
  }
  return {}
}

/**
 * RN-03.4 — El TOTAL de un subárbol: los JOB que cuentan en el censo (`countsInCensus`, los
 * vinculados con vínculo vivo), a todos los niveles. `partial` avisa de que dentro hay una rama no
 * legible, cuyos JOB no se han podido contar (`RN-01.3b`, S6): la cifra es parcial y la UI lo dice.
 *
 * La otra mitad de la cobertura —cuántos de esos ya están en el workflow— la añade J5 con el censo.
 *
 * @param {ProcessPaletteRow[]} rows
 * @returns {{ total: number, partial: boolean }}
 */
export const censusTotalOf = (rows = []) => {
  let total = 0
  let partial = false
  const walk = (list) => {
    list.forEach((row) => {
      if (row.unreadable) {
        partial = true
        return
      }
      if (row.countsInCensus) {
        total++
      }
      walk(row.children || [])
    })
  }
  walk(rows || [])
  return { total, partial }
}

/**
 * RN-03.4/RN-03.5 — La COBERTURA de un subárbol: cuántas de sus operaciones de librería están ya
 * en el workflow en edición y cuántas hay en total. Es el indicador "{{present}} of {{total}}
 * operations added" (J11) de la fila raíz y de cada subproceso (S74).
 *
 * El numerador lo pone el censo (E3, `compositionCensus.js`) y el denominador, `censusTotalOf`:
 * los dos cuentan SOLO JOB vinculados (`countsInCensus`), así que un JOB local no aparece ni
 * arriba ni abajo (S75, D11-bis).
 *
 * `noLibraryOperations` es la declaración de `RN-03.4b` (J40, "aquí no hay nada que traer"), y
 * **solo** se afirma cuando todo el subárbol está cargado: con una rama no legible manda el aviso
 * de rama no cargada y la cifra se declara parcial (`RN-03.4c`, S78, S81). No se puede afirmar
 * sobre lo que no se ha leído.
 *
 * @param {ProcessPaletteRow[]} rows
 * @param {import('./compositionCensus').CompositionCensus} census
 * @returns {{ present: number, total: number, partial: boolean, noLibraryOperations: boolean }}
 */
export const censusCoverageOf = (rows = [], census = {}) => {
  const { total, partial } = censusTotalOf(rows)
  let present = 0
  const walk = (list) => {
    list.forEach((row) => {
      if (row.unreadable) {
        return
      }
      if (row.countsInCensus && census?.isPresent?.(row)) {
        present++
      }
      walk(row.children || [])
    })
  }
  walk(rows || [])
  return { present, total, partial, noLibraryOperations: total === 0 && !partial }
}

/**
 * RN-03.8/RN-03.8b — Las filas que quedan con el filtro "pendientes de añadir" (J8) encendido, en
 * un árbol nuevo: este módulo no muta nada (S83).
 *
 * Tres reglas, y ninguna más:
 *   1. Un JOB **vinculado** se queda solo si NO está presente: eso es estar pendiente.
 *   2. Un JOB **local** se oculta siempre: no está en el censo, así que no puede estar pendiente.
 *   3. Una rama se queda SIEMPRE —vacía si hace falta, marcada con `filteredEmpty` para que la
 *      sección la declare vacía en vez de dejarla desaparecer en silencio (S83)— y una rama **no
 *      legible** se queda con su aviso (J12): no se puede afirmar que no queda nada pendiente
 *      dentro de lo que no se ha leído (`RN-03.4c`, S81).
 *
 * @param {ProcessPaletteRow[]} rows
 * @param {import('./compositionCensus').CompositionCensus} census
 * @returns {ProcessPaletteRow[]}
 */
export const pendingProcessRows = (rows = [], census = {}) => (rows || []).reduce((kept, row) => {
  if (row.unreadable) {
    kept.push(row)
    return kept
  }
  if (row.nodeType === 'WORKFLOW') {
    const children = pendingProcessRows(row.children || [], census)
    kept.push({ ...row, children, filteredEmpty: children.length === 0 })
    return kept
  }
  if (row.countsInCensus && !census?.isPresent?.(row)) {
    kept.push(row)
  }
  return kept
}, [])

/**
 * S12/S15/S20 — Lo que la fila le dice al arrastre. Es el ÚNICO sitio donde se decide qué nace de
 * cada fila, y el único que sella el proceso de origen: el código de la plantilla RAÍZ del árbol
 * desplegado, nunca el del subproceso intermedio que contenga el JOB (`RN-01.10`, S13).
 *
 * Una rama no legible no produce carga: no se arrastra (S6).
 *
 * @param {ProcessPaletteRow} row
 * @param {{ code: string }} tree El árbol proyectado, del que sale el proceso de origen
 * @returns {Object} La carga del arrastre, o `{}` si la fila no se arrastra
 */
export const toProcessDropPayload = (row = {}, tree = {}) => {
  if (!row?.nodeId || row.draggable === false) {
    return {}
  }
  const kind = row.nodeType === 'WORKFLOW'
    ? PROCESS_ROW_DROP_KIND.PROCESS
    : row.linked ? PROCESS_ROW_DROP_KIND.LINKED_JOB : PROCESS_ROW_DROP_KIND.LOCAL_JOB
  return {
    kind,
    nodeId: row.nodeId,
    code: row.code,
    name: row.name,
    // El workflow que CONTIENE el nodo: lo que `intent: 'COPY'` manda como `sourceWorkflowId`.
    workflowId: row.workflowId,
    subWorkflowId: row.subWorkflowId,
    operationCode: row.operationCode,
    operationVersionNumber: row.operationVersionNumber,
    // S95 (D12, `RN-01.10b`, `IN16`): el PAR de la plantilla raíz, indivisible. La versión sale de
    // la MISMA fila que ofrece la paleta —la que el árbol declara—, nunca de "la vigente".
    ...toSourceProcessPair(tree?.code, tree?.processVersionNumber)
  }
}

/**
 * Selección múltiple con subprocesos — la fila que se puede MARCAR: un JOB arrastrable (un JOB
 * vinculado solo con `OPERATION_LIBRARY`, `RN-G16`) o una rama WORKFLOW legible con subproceso que
 * incorporar. Una rama marcada viaja como subproceso COMPLETO, que es la plantilla y no la
 * biblioteca: por eso no depende del permiso.
 *
 * @param {ProcessPaletteRow} row
 * @param {boolean} canReadOperationLibrary
 * @returns {boolean}
 */
export const isSelectableProcessRow = (row = {}, canReadOperationLibrary = false) => {
  if (!row?.draggable) {
    return false
  }
  if (row.nodeType === 'WORKFLOW') {
    return !row.unreadable && (row.subWorkflowId || 0) > 0
  }
  return canReadOperationLibrary || !row.linked
}

/**
 * Marca o desmarca una fila y devuelve la selección NUEVA (no muta la recibida):
 *   - marcar/desmarcar una rama arrastra consigo a todas sus filas marcables, a cualquier
 *     profundidad: marcar un subproceso es marcar todo lo que lleva dentro;
 *   - desmarcar cualquier fila desmarca a todas las ramas que la contienen: ya no están enteras, así
 *     que lo que se arrastra son solo las filas que siguen marcadas;
 *   - marcar la última fila que faltaba NO vuelve a marcar la rama: arrastrar un subproceso es
 *     siempre una decisión explícita del usuario.
 *
 * @param {ProcessPaletteRow[]} rows
 * @param {Object<string, boolean>} checked La selección actual, por `nodeId`
 * @param {string} nodeId
 * @param {boolean} value
 * @param {boolean} canReadOperationLibrary
 * @returns {Object<string, boolean>}
 */
export const toggleProcessRowSelection = (rows = [], checked = {}, nodeId = '', value = false, canReadOperationLibrary = false) => {
  const next = { ...checked }
  const markSubtree = (row) => {
    if (isSelectableProcessRow(row, canReadOperationLibrary)) {
      next[row.nodeId] = value
    }
    (row.children || []).forEach(markSubtree)
  }
  const walk = (list, ancestors) => {
    for (const row of list || []) {
      if (row.nodeId === nodeId) {
        markSubtree(row)
        if (!value) {
          ancestors.forEach((ancestor) => { next[ancestor.nodeId] = false })
        }
        return true
      }
      if (walk(row.children || [], [...ancestors, row])) {
        return true
      }
    }
    return false
  }
  walk(rows, [])
  return next
}

/**
 * Lo que se suelta al arrastrar la selección, en el orden del árbol: una rama marcada viaja ENTERA
 * como subproceso —sin desglosar sus filas, que ya van dentro— y fuera de ella, cada JOB marcado.
 *
 * @param {ProcessPaletteRow[]} rows
 * @param {Object<string, boolean>} checked
 * @param {boolean} canReadOperationLibrary
 * @returns {ProcessPaletteRow[]}
 */
export const selectedProcessRows = (rows = [], checked = {}, canReadOperationLibrary = false) => {
  const selected = []
  const walk = (list) => {
    (list || []).forEach((row) => {
      const marked = checked[row.nodeId] === true && isSelectableProcessRow(row, canReadOperationLibrary)
      if (row.nodeType === 'WORKFLOW' && marked) {
        selected.push(row)
        return
      }
      if (marked) {
        selected.push(row)
      }
      walk(row.children || [])
    })
  }
  walk(rows)
  return selected
}

/**
 * Story 167 (CA5.1 · RN-07.1): how many DISTINCT operations (JOB rows, by `nodeId`) hang from these rows at
 * any depth. `known` is false as soon as one branch could not be read: a partial count is not given as whole.
 *
 * @param {ProcessPaletteRow[]} rows
 * @returns {{ known: boolean, count: number }}
 */
export const countProcessOperations = (rows = []) => {
  const jobIds = new Set()
  let known = true
  const walk = (list) => {
    (list || []).forEach((row) => {
      if (row.unreadable) {
        known = false
        return
      }
      if (row.nodeType === 'JOB') {
        jobIds.add(row.nodeId)
      }
      walk(row.children)
    })
  }
  walk(rows)
  return { known, count: jobIds.size }
}

/**
 * Story 152, Enmienda 1 (S20 · `RN-01.10b` punto 2, D12), moved here from `NodeTypesBar.jsx:80-91` when the
 * palette went away: a template row dragged WITHOUT unfolding seals the origin pair with the version the
 * template list already publishes.
 */
export const templateDragPropertiesOf = (process = {}) => ({
  'application/reactflow/nodeType': 'workflow',
  'application/reactflow/subType': process.id,
  'application/reactflow/sourceProcessCode': process.code || '',
  'application/reactflow/sourceProcessVersionNumber': process.version ?? null
})
